import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, formatNumber } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { CellClickedEvent, ColDef, GridOptions } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { myCustomTheme } from '@constants/grid-themes';
import { ReviewBoardListItem } from '@models/review-board.model';
import { ReviewBoardActions } from '@store/actions/review-board/review-board.actions';
import {
  selectReviewBoardList,
  selectReviewBoardListLoading,
} from '@store/selectors/review-board/review-board.selectors';
import { MipsReviewActionsRendererComponent } from './mips-review-actions-renderer.component';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-mips-review-admin',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AgGridAngular],
  templateUrl: './mips-review-admin.component.html',
  styleUrl: './mips-review-admin.component.css',
})
export class MipsReviewAdminComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  theme = myCustomTheme;

  hideSubmittedControl = new FormControl(false);

  rowData$ = combineLatest([
    this.store.select(selectReviewBoardList),
    this.hideSubmittedControl.valueChanges.pipe(startWith(false)),
  ]).pipe(
    map(([items, hideSubmitted]) =>
      hideSubmitted ? items.filter((item) => item.status !== 'Submitted') : items
    )
  );
  loading$ = this.store.select(selectReviewBoardListLoading);

  public gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 20,
    paginationPageSizeSelector: [10, 20, 50],
    suppressClickEdit: true,
  };

  colDefs: ColDef[] = [
    { field: 'organizationName', headerName: 'Organization Name', flex: 2, minWidth: 160 },
    { field: 'legacyOrganizationId', headerName: 'ID', flex: 0.6, minWidth: 70 },
    {
      headerName: 'Type',
      flex: 0.8,
      minWidth: 90,
      valueGetter: (params: any) =>
        params.data?.entityType === 'individual' ? 'Individual' : 'Group',
    },
    {
      headerName: 'Provider',
      flex: 1.4,
      minWidth: 140,
      valueGetter: (params: any) => {
        const item = params.data as ReviewBoardListItem;
        return item?.entityType === 'individual'
          ? `${item.providerName ?? ''} - ${item.npi ?? ''}`
          : 'All Providers';
      },
    },
    {
      headerName: 'Submission Type',
      flex: 0.9,
      minWidth: 120,
      valueGetter: (params: any) =>
        params.data?.submissionType === 'mvp' ? 'MVP' : 'Traditional',
    },
    { field: 'requestedByUserName', headerName: 'Submitted By', flex: 1, minWidth: 110 },
    { field: 'status', headerName: 'Status', flex: 0.8, minWidth: 90 },
    {
      field: 'qualityScore',
      headerName: 'Quality',
      flex: 0.7,
      minWidth: 90,
      valueFormatter: (params: any) => this.formatScore(params.value),
    },
    {
      field: 'iaScore',
      headerName: 'IA',
      flex: 0.7,
      minWidth: 90,
      valueFormatter: (params: any) => this.formatScore(params.value),
    },
    {
      field: 'piScore',
      headerName: 'PI',
      flex: 0.7,
      minWidth: 90,
      valueFormatter: (params: any) => this.formatScore(params.value),
    },
    {
      headerName: 'Status Date',
      flex: 0.9,
      minWidth: 110,
      valueGetter: (params: any) =>
        params.data?.statusDate
          ? new Date(params.data.statusDate).toLocaleDateString('en-US')
          : '',
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      minWidth: 100,
      width: 100,
      pinned: 'right',
      filter: false,
      sortable: false,
      resizable: false,
      tooltipValueGetter: () => '',
      cellRenderer: MipsReviewActionsRendererComponent,
    },
  ];

  defaultColDef: ColDef = {
    filter: true,
    resizable: true,
    sortable: true,
    editable: false,
    minWidth: 80,
    tooltipValueGetter: (params: any) => (params.value != null ? String(params.value) : ''),
  };

  ngOnInit(): void {
    this.store.dispatch(ReviewBoardActions.loadList());
  }

  formatScore(value: number | null): string {
    return value != null ? formatNumber(value, 'en-US') : '—';
  }

  onCellClicked(params: CellClickedEvent<ReviewBoardListItem>): void {
    if (params.column.getColId() !== 'actions' || !params.data) return;
    const actionElement = (params.event?.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]');
    if (actionElement?.dataset['action'] !== 'open') return;

    this.router.navigate(['/mipsattest'], {
      queryParams: { openSubmissionId: params.data.submissionId, fromAdminReview: true },
    });
  }
}
